import { eventDirectoryLocation } from "pages/EventsManagement/directoryQuery"
import React, { useState, useEffect, useCallback } from "react"
import {
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  Col,
  TabPane,
  TabContent,
  Breadcrumb,
  BreadcrumbItem,
} from "reactstrap"
import classnames from "classnames"
import { connect } from "react-redux"
import * as actions from "../actions"
import { Link } from "react-router-dom"
import Tabs from "./Tabs"
import CreateEvent from "../CreateEvent"
import { get } from "helpers/api_helper"
import EventActions from "../EventActions"
import EventStage from "../EventStage"
import EventFeesWarning from "../EventFeesWarning"
import { invalidateEventCache } from "../eventCachePolicy"
import { getEventDetailsReadModeForTab } from "../eventDetailsReadPolicy"
import { canAccessEventTab, canCreateEvent } from "helpers/cpAccessPolicy"
import "../eventDetails.scss"
import { eventPlaces } from "../mainListPlaces"

export const Event = ({
  getEvents,
  getEventDetails,
  userDetails,
  eventDetails = [],
  error = "",
  history,
  match: { params: { id, tab: urlTab } = {} } = {},
}) => {
  const [activeTabState, setActiveTab] = useState("details")

  // A tab the current role cannot open, or an unknown one, falls back to Event details.
  const tabs = Tabs.filter(tab => canAccessEventTab(userDetails, tab))
  const requestedTab = urlTab || activeTabState
  const activeTab = tabs.some(({ url }) => url === requestedTab) ? requestedTab : tabs[0].url
  const eventDetailsReadMode = getEventDetailsReadModeForTab(activeTab)

  const toggle = tab => {
    if (activeTab !== tab) setActiveTab(tab)
  }
  const [loadedEvent] = eventDetails
  const activeEvent = loadedEvent?._id === id ? loadedEvent : null
  // The event details read: its `event` heads the page, its `details` (category, phases, club) date the fee warning.
  const [header, setHeader] = useState(null), [headerDetails, setHeaderDetails] = useState(null)
  const [editing, setEditing] = useState(false)
  const [refresh, setRefresh] = useState(0)
  useEffect(() => {
    let current = true; setHeader(null); setHeaderDetails(null)
    if (/^[a-f\d]{24}$/i.test(id || '')) get('/admin/event-details/' + id).then(response => {
      if (current && response.data?.id === id) { setHeader(response.data.event); setHeaderDetails(response.data.details || null) }
    }).catch(() => {})
    return () => { current = false }
  }, [id, activeTab, refresh])
  const refreshEvent = useCallback(() => {
    invalidateEventCache(id); getEventDetails(id, eventDetailsReadMode); setRefresh(value => value + 1)
  }, [id, eventDetailsReadMode, getEventDetails])
  const eventUnavailable = Boolean(id && error && !activeEvent?._id)
  const isLoadingEvent = Boolean(id && !activeEvent?._id && !eventUnavailable)

  useEffect(() => {
    if (id) {
      getEventDetails(id, eventDetailsReadMode)
    } else {
      getEventDetails("empty")
    }
    return () => getEventDetails("empty")
  }, [eventDetailsReadMode, getEventDetails, id])

  const ActiveTab = tabs.find(({ url }) => url === activeTab).Component

  const checkMaintenance = () => (header?.maintenance ?? activeEvent?.maintenance) === true

  const count = value => Number.isFinite(value) ? value : "—"
  const totals = header?.entryTotals || activeEvent?.entryTotals || {}
  const places = eventPlaces(totals, header?.entryTotals ? header.competitions : activeEvent?.competitions)

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb>
          <BreadcrumbItem>
            <Link to={eventDirectoryLocation()}>Events</Link>
          </BreadcrumbItem>
          {activeEvent?._id ? (
            <BreadcrumbItem active>Event: {activeEvent?.name}</BreadcrumbItem>
          ) : isLoadingEvent ? (
            <BreadcrumbItem active>Event: Loading...</BreadcrumbItem>
          ) : eventUnavailable ? (
            <BreadcrumbItem active>Event unavailable</BreadcrumbItem>
          ) : (
            <BreadcrumbItem active>Create New Event</BreadcrumbItem>
          )}
        </Breadcrumb>
        <div className="d-flex justify-content-between flex-wrap">
          <div>
            {activeEvent?.name ? (
              <h4>
                {activeEvent?.name +
                  " - " +
                  activeEvent?.category?.charAt(0)?.toUpperCase() +
                  activeEvent?.category?.slice(1) +
                  " - Id: " +
                  activeEvent?.eventId}{" "}
                {checkMaintenance()
                  ? "(MAINTENANCE)"
                  : activeEvent?.status === "DISABLED"
                  ? "(DISABLED)"
                  : "(ACTIVE)"}
              </h4>
            ) : isLoadingEvent ? (
              <h4>Loading event...</h4>
            ) : eventUnavailable ? (
              <h4>Could not load event.</h4>
            ) : (
              <h4>New Event</h4>
            )}
            {id && !isLoadingEvent && !eventUnavailable ? (
              <div className="d-flex flex-wrap gap-10 align-items-center mb-3">
                <h6 className="m-0">Total Entries</h6>
                <div className="badge badge-success p-2">
                  {`${count(places.taken)} / ${count(totals.capacity)}`} ML
                </div>
                <div className="badge badge-warning p-2">
                  {count(totals.waitingList)} WL
                </div>
              </div>
            ) : null}
          </div>

          {id && !eventUnavailable && <EventStage stage={activeEvent?.currentPhase?.status} loading={isLoadingEvent} />}
        </div>
        {id && !eventUnavailable && <EventFeesWarning eventId={id} details={headerDetails} event={header} onCompetitionsTab={activeTab === "competitions"} />}
        <div className="tabs">
          {id && (
            <div className="event-header-toolbar">
              <Nav className="event-header-tabs mb-0" pills aria-label="Event sections">
                {tabs.map(({ label, url }) => {
                  return (
                    <NavItem key={label}>
                      <NavLink
                        className={classnames({
                          active: activeTab === url,
                        })}
                        onClick={() => {
                          urlTab
                            ? history.push(`/events/${id}/${url}`)
                            : toggle(url)
                        }}
                      >
                        {label}
                      </NavLink>
                    </NavItem>
                  )
                })}
              </Nav>
              {activeEvent?._id && <EventActions event={{ ...activeEvent, ...(header || {}) }} user={userDetails} disabled={editing} onChanged={type => {
                if (type === 'deleted') { invalidateEventCache(id); getEvents(); history.push(eventDirectoryLocation()) }
                else refreshEvent()
              }} />}

            </div>
          )}
          <TabContent activeTab={activeTab}>
            <TabPane tabId={activeTab}>
              <Row>
                <Col sm="12">
                  {id ? <ActiveTab
                    id={id}
                    userRole={userDetails?.role}
                    actorId={userDetails?.id || userDetails?._id}
                    getEvents={getEvents}
                    history={history}
                    activeEvent={activeEvent}
                    refreshVersion={refresh}
                    onSaved={refreshEvent}
                    onEditing={setEditing}
                  /> : canCreateEvent(userDetails) ? <CreateEvent history={history} />
                    : <div className="alert alert-warning" role="alert">Only a SuperAdmin can create events.</div>}
                </Col>
              </Row>
            </TabPane>
          </TabContent>
        </div>
      </Container>
    </div>
  )
}

const mapStatetoProps = ({
  events: { events = [], eventDetails = [], error = "" } = {},
  session: { userDetails } = {},
}) => ({
  events,
  userDetails,
  eventDetails,
  error,
})

export default connect(mapStatetoProps, {
  getEvents: actions.getEvents,
  getEventDetails: actions.getEventDetails,
  setEventDetailsStatus: actions.setEventDetailsStatus,
})(Event)
